// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames';
import ShimmerButton from './ShimmerButton';

interface AutoProgressButtonProps {
    children: React.ReactNode;
    onAutoProgress?: () => void;
    onClick?: () => void;
    autoProgressDelay?: number; // in milliseconds
    className?: string;
    disabled?: boolean;
}

export default function AutoProgressButton({
    children,
    onAutoProgress,
    onClick,
    autoProgressDelay = 3000,
    className,
    disabled,
    ...props
}: AutoProgressButtonProps) {
    const [timeRemaining, setTimeRemaining] = useState(
        Math.ceil(autoProgressDelay / 1000)
    );
    const [progress, setProgress] = useState(0);
    const [isProgressing, setIsProgressing] = useState(true);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const animationRef = useRef<number | null>(null);
    const startTimeRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isProgressing || disabled) return;

        // Start the animation
        startTimeRef.current = Date.now();

        // Update countdown every second
        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - (startTimeRef.current || Date.now());
            const remaining = Math.max(0, autoProgressDelay - elapsed);
            const secondsRemaining = Math.ceil(remaining / 1000);

            setTimeRemaining(secondsRemaining);

            if (remaining <= 0) {
                if (intervalRef.current) clearInterval(intervalRef.current);
                handleAutoProgress();
            }
        }, 100); // Check every 100ms for more accurate timing

        // Animate the progress ring
        const animateProgress = () => {
            if (!startTimeRef.current) return;

            const elapsed = Date.now() - startTimeRef.current;
            const currentProgress = Math.min(
                100,
                (elapsed / autoProgressDelay) * 100
            );

            setProgress(currentProgress);

            if (currentProgress < 100 && isProgressing) {
                animationRef.current = requestAnimationFrame(animateProgress);
            }
        };

        animationRef.current = requestAnimationFrame(animateProgress);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (animationRef.current)
                cancelAnimationFrame(animationRef.current);
        };
    }, [autoProgressDelay, isProgressing, disabled, onAutoProgress]);

    const handleAutoProgress = () => {
        setIsProgressing(false);
        onAutoProgress?.();
    };

    const handleClick = () => {
        // Cancel auto-progress if user clicks manually
        setIsProgressing(false);
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        onClick?.();
    };

    return (
        <div className="relative inline-block">
            {/* Button with countdown */}
            <ShimmerButton
                className={classNames('relative overflow-hidden', className)}
                onClick={handleClick}
                disabled={disabled}
                aria-label={
                    isProgressing
                        ? `Auto-continuing in ${timeRemaining} seconds`
                        : undefined
                }
                {...props}
            >
                <span className="flex items-center justify-center gap-2 relative z-20">
                    {children}
                    {isProgressing && !disabled && (
                        <motion.span
                            key={timeRemaining}
                            initial={{ scale: 1.2, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="text-pink-200 font-bold text-sm"
                        >
                            ({timeRemaining}s)
                        </motion.span>
                    )}
                </span>
            </ShimmerButton>

            {/* Subtle pulse effect during countdown */}
            <AnimatePresence>
                {isProgressing && !disabled && (
                    <motion.div
                        className="absolute inset-0 rounded-lg pointer-events-none -z-10"
                        initial={{ opacity: 0, scale: 1 }}
                        animate={{
                            opacity: [0, 0.15, 0],
                            scale: [1, 1.02, 1],
                        }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                        }}
                        style={{
                            background:
                                'radial-gradient(circle, rgba(236, 72, 153, 0.4) 0%, transparent 70%)',
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
